import { computed, onBeforeMount, readonly, ref, Ref } from 'vue';
import { PaginationMeta } from '@/api/openapi';
import { useWait } from './vue-wait';

const useInfiniteScroll = <TItem>(
  getItems: () => TItem[],
  limit: number,
) => {
  const loadedItems = ref<TItem[]>([]);
  const currentPage = ref(0);

  const itemsLength = computed(() => getItems().length);
  const loadedItemsLength = computed(() => loadedItems.value.length);
  const scrollDisabled = computed(() => itemsLength.value <= loadedItemsLength.value);

  const loadMore = () => {
    if (scrollDisabled.value) return;

    const end = itemsLength.value < loadedItemsLength.value + limit
      ? itemsLength.value
      : loadedItemsLength.value + limit;
    loadedItems.value = (loadedItems.value as TItem[]).concat(
      getItems().slice(loadedItemsLength.value, end),
    );
    currentPage.value += 1;
  };
  const reset = () => {
    loadedItems.value = [];
    currentPage.value = 0;
  };

  const loadAll = (): Promise<void> => {
    const totalItems = getItems().length;
    const requiredCalls = Math.ceil(totalItems / limit);

    if (requiredCalls === 0) return Promise.resolve();

    return new Promise((resolve) => {
      let completedCalls = 0;

      for (let i = 0; i < requiredCalls; i++) {
        setTimeout(() => {
          if (!scrollDisabled.value) {
            loadMore();
          }

          completedCalls++;
          if (completedCalls === requiredCalls) {
            resolve();
          }
        }, i * 10); // 10ms間隔で実行
      }
    });
  };

  return {
    loadedItems: readonly(loadedItems),
    scrollDisabled,
    loadMore,
    loadAll,
    reset,
  };
};

const useInfiniteLoading = <TItem>(
  fetchItems: (page: number) => Promise<{ items: TItem[]; paginationMeta: PaginationMeta } | null>,
  keyOfWait: string,
  initialScrollDisabled = true,
) => {
  const { doActionWithWait } = useWait();

  // NOTE: 解決される型が理想と違うので変換
  // @see https://github.com/vuejs/core/issues/2136
  const items = ref<TItem[]>([]) as Ref<TItem[]>;
  const paginationMeta = ref<PaginationMeta>(null);
  const currentPage = ref(1);
  const infiniteScrollDisabled = ref(initialScrollDisabled);

  const fetchAndConcatItems = async (page = 1) => {
    await doActionWithWait(keyOfWait, async () => {
      try {
        const result = await fetchItems(page);
        if (result == null) return;

        paginationMeta.value = result.paginationMeta;

        const previous = items.value.slice(0, (page - 1) * paginationMeta.value.limitValue);
        const following = items.value.slice(page * paginationMeta.value.limitValue);
        items.value = previous.concat(result.items, following);
      } catch (e) {
        if (e.response?.status === 404) {
          items.value = [];
        }
        throw e;
      }
    });
  };
  const loadMore = async () => {
    if ((paginationMeta.value?.totalCount ?? 0) <= items.value.length) return;

    infiniteScrollDisabled.value = true;
    const nextPage = currentPage.value + 1;
    await fetchAndConcatItems(nextPage);
    currentPage.value = nextPage;

    infiniteScrollDisabled.value = false;
  };
  const clear = () => {
    // これがないと、「非表示 -> 表示」無限スクロールが発火してしまって、複数リクエストが実行されてしまう。
    infiniteScrollDisabled.value = true;

    currentPage.value = 1;
    items.value = [];
  };
  const handleAfterOpen = () => {
    fetchAndConcatItems()
      .then(() => {
        infiniteScrollDisabled.value = false;
      })
      .catch((e) => { throw e; });
  };
  const fetchLoadedPage = () => {
    for (let i = 1; i <= currentPage.value; i++) {
      fetchAndConcatItems(i).catch((e) => { throw e; });
    }
  };
  const refetch = async () => {
    clear();
    await fetchAndConcatItems();
    infiniteScrollDisabled.value = false;
  };

  onBeforeMount(() => {
    fetchAndConcatItems().catch((e) => { throw e; });
  });

  return {
    items: readonly(items),
    paginationMeta: readonly(paginationMeta),
    infiniteScrollDisabled: readonly(infiniteScrollDisabled),
    loadMore,
    clear,
    handleAfterOpen,
    fetchLoadedPage,
    refetch,
  };
};

export {
  useInfiniteScroll,
  useInfiniteLoading,
};
